import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';
import { hooks } from '@lifesize/clients.sdk';
import { callActions as nucleusCallActions } from '@lifesize/nucleus';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as MicIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import { ReactComponent as MicOffIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import { makeStyles, Theme, Tooltip } from '@material-ui/core';

interface Props {
  participant: any;
}

export const getMicConfig = (participant, userIsModerator, classes) => {
  const { localAudioMuteState, serverAudioMuteState } = participant;

  const config = {
    currentDataTestId: serverAudioMuteState || localAudioMuteState ? 'participantBtnMicOff' : 'participantBtnMicOn',
    currentIcon: serverAudioMuteState || localAudioMuteState ? MicOffIcon : MicIcon,
    currentIconClasses: clsx(
      classes.buttonIcon,
      userIsModerator && localAudioMuteState && !serverAudioMuteState ? classes.selfMuted : ''
    ),
    menuOptionDataTestId: '',
    menuOptionIcon: MicIcon,
    menuOptionLabel: <> </>
  };

  if (serverAudioMuteState) {
    config.menuOptionDataTestId = 'moreMenuOptionMicOn';
    config.menuOptionIcon = MicIcon;
    config.menuOptionLabel = (
      <FormattedMessage
        id="components.inCall.participantDrawer.removeModeratorMute"
        defaultMessage="Remove moderator mute"
      />
    );
  } else {
    // !serverAudioMuteState
    config.menuOptionDataTestId = 'moreMenuOptionMicOff';
    config.menuOptionIcon = MicOffIcon;
    config.menuOptionLabel = (
      <FormattedMessage id="components.inCall.participantDrawer.addModeratorMute" defaultMessage="Add moderator mute" />
    );
  }

  return config;
};

export const ParticipantMicButton = ({ participant }: Props) => {
  const classes = useStyles({});
  const userIsModerator = hooks.useIsCurrentUserModerator();
  const conferenceInfo = hooks.useConferenceInfo();
  const canBeMuted = userIsModerator && conferenceInfo?.state === 'escalated';
  const { isSelf } = participant;
  const dispatch = useDispatch();
  const micConfig = getMicConfig(participant, userIsModerator, classes);
  const handleMuteParticipantClicked = (participant) => {
    dispatch(nucleusCallActions.muteParticipant(participant, !participant.serverAudioMuteState));
  };
  const CurrentIcon = micConfig.currentIcon;

  if (userIsModerator && canBeMuted && !isSelf) {
    return (
      <Tooltip arrow={true} placement="top-end" title={micConfig.menuOptionLabel}>
        <button
          className={classes.button}
          onClick={() => handleMuteParticipantClicked(participant)}
          data-testid={micConfig.currentDataTestId}
        >
          <CurrentIcon className={micConfig.currentIconClasses} />
        </button>
      </Tooltip>
    );
  }

  // non-moderators just see an indicator
  return (
    <div className={`${classes.button} ${classes.noButton}`} data-testid={micConfig.currentDataTestId}>
      <CurrentIcon className={micConfig.currentIconClasses} />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    alignItems: 'center',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    fontSize: '1em', // prevents global button default
    height: '100%', // necessary for the drop-down button
    justifyContent: 'center',
    margin: '0 3px',
    outline: 'none',
    padding: 0,
    position: 'relative',
    width: '2em'
  },
  noButton: {
    cursor: 'default'
  },
  buttonIcon: {
    height: '2em',
    width: 'auto'
  },
  selfMuted: {
    color: colors.alertRed
  }
}));

export default ParticipantMicButton;
